import { GridFilterItem } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';

import { Region } from '../../../@types/geosocial.type';
import { useGetRegionsList } from '../../../queries/geosocial-query';

export interface ZoneSuggestion {
  locality: string;
  /** The zone this locality would put the structure in. */
  geoName: string;
}

export interface StructureZoneSuggestionsArgs {
  /**
   * `AddressDataType` types these as strings, but the wire does not honour that
   * — a postcode arrives as a number often enough to have crashed this hook —
   * so they are read defensively rather than trusted.
   */
  postcode?: string | number | null;
  /** Scopes the lookup — `geosocial.region` is unique on postCode + locality + state + country. */
  state?: string | number | null;
  country?: string | number | null;
}

/** Coerces a wire value that is typed `string` but is not always one. */
const asTrimmedString = (value?: string | number | null): string =>
  value === null || value === undefined ? '' : String(value).trim();

export interface StructureZoneSuggestions {
  isLoading: boolean;
  /** Localities `geosocial.region` knows for the address's postcode. */
  suggestions: ZoneSuggestion[];
  /** True once the lookup has succeeded and found no region for the postcode. */
  postcodeUnknown: boolean;
  /** The lookup failed. Distinct from "no regions" — one is data, the other is a fault. */
  lookupFailed: boolean;
  /** More localities exist than were fetched, so the list shown is partial. */
  isTruncated: boolean;
}

/**
 * A postcode maps to a handful of localities, not hundreds. The cap is a
 * backstop rather than paging, and `isTruncated` surfaces it rather than
 * silently showing a partial list.
 */
const PAGE_SIZE = 100;

/**
 * Works out why a structure has no zone, and what would fix it.
 *
 * Zones are not stored on the structure — `api-geosocial` derives `node.zone` by
 * matching the node's address against `geosocial.region`, which is unique on
 * (postCode, locality, state, country). So an unzoned structure almost always
 * means its suburb and postcode do not together match a region row: usually a
 * misspelled or renamed suburb.
 *
 * Looking the postcode up tells the user both things they need — which suburbs
 * that postcode is actually known by, and which zone each would produce.
 *
 * Deliberately no fuzzy widening to neighbouring postcodes. `api-geosocial`
 * tolerates a one-digit difference in its own matching, but suggesting a user
 * re-point an address at a *different* postcode invents geography and makes the
 * data worse — if the postcode itself has no regions, the region table is what
 * needs the edit.
 */
export const useStructureZoneSuggestions = ({
  postcode,
  state,
  country,
}: StructureZoneSuggestionsArgs): StructureZoneSuggestions => {
  const trimmedPostcode = asTrimmedString(postcode);
  const trimmedState = asTrimmedString(state);
  const trimmedCountry = asTrimmedString(country);

  // Filtered on the postcode alone. Scoping the *query* by state or country as
  // well looks tempting — a postcode can exist in more than one country — but
  // the address stores display names (`stateName` is "Victoria") while the
  // region table stores its own representation ("VIC"), so a mismatched
  // narrowing returns nothing and the banner silently stops suggesting
  // anything. Jurisdiction is applied below as a preference instead, where
  // getting it wrong costs nothing.
  const filterItems: GridFilterItem[] = trimmedPostcode
    ? [{ field: 'postCode', operator: 'equals', value: trimmedPostcode }]
    : [];

  const regionsQuery = useGetRegionsList(
    { page: 0, pageSize: PAGE_SIZE, filterModel: { items: filterItems } },
    // Without a postcode there is nothing to look up, and an empty filter model
    // would fetch the first page of every region in the system.
    !!trimmedPostcode
  );

  return useMemo(() => {
    // Bail out before reading the query at all. `enabled: false` stops new
    // fetches but a disabled observer still hands back whatever is cached under
    // its key, and the key for an empty postcode is a perfectly cacheable
    // `{ items: [] }` filter — so a hit from elsewhere in the app would have
    // become "suggestions" drawn from every region in the table.
    if (!trimmedPostcode) {
      return {
        isLoading: false,
        suggestions: [],
        postcodeUnknown: false,
        lookupFailed: false,
        isTruncated: false,
      };
    }

    const rows: Region[] = regionsQuery.data?.data ?? [];
    // Region rows are wire data too, so every field is coerced before a string
    // method touches it — the same assumption crashed the Location tab once.
    const active = rows.filter((row) => row.isActive && !!asTrimmedString(row.locality));

    // Prefer the address's own jurisdiction when it demonstrably matches, but
    // never let that preference empty the list — the two sides spell states and
    // countries differently often enough that an exact match is not guaranteed.
    const equalsIgnoringCase = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
    const matchesJurisdiction = (row: Region) =>
      (!trimmedState || equalsIgnoringCase(asTrimmedString(row.state), trimmedState)) &&
      (!trimmedCountry || equalsIgnoringCase(asTrimmedString(row.country), trimmedCountry));
    const inJurisdiction = active.filter(matchesJurisdiction);
    const candidates = inJurisdiction.length > 0 ? inJurisdiction : active;

    // A postcode usually spans several localities, and more than one can carry
    // the same zone; show each locality once.
    const seen = new Set<string>();
    const suggestions = candidates.reduce<ZoneSuggestion[]>((acc, row) => {
      const locality = asTrimmedString(row.locality);
      const key = locality.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        acc.push({ locality, geoName: asTrimmedString(row.geoName) });
      }
      return acc;
    }, []);

    const total = regionsQuery.data?.total ?? rows.length;

    return {
      isLoading: regionsQuery.isLoading && !!trimmedPostcode,
      suggestions,
      // Only a *successful* empty result means the postcode is unknown. A failed
      // request must not be reported as "no region is set up" — that sends the
      // user off to create master data that may already exist.
      postcodeUnknown: regionsQuery.isSuccess && !!trimmedPostcode && rows.length === 0,
      lookupFailed: regionsQuery.isError,
      isTruncated: total > rows.length,
    };
  }, [
    regionsQuery.data,
    regionsQuery.isLoading,
    regionsQuery.isSuccess,
    regionsQuery.isError,
    trimmedPostcode,
    trimmedState,
    trimmedCountry,
  ]);
};
